import { useState, useEffect, useCallback } from 'react';
import {
  GET_RESOURCE_ROLES,
  GET_ROLE_ASSIGNMENTS,
  UPDATE_ROLE_ASSIGNMENTS
} from '../api/videoShareSettingQueries';

export type Role = {
  roleId: string;
  name: string;
};

export type WorkspaceSettings = {
  role: Role;
  canSearch: boolean;
  resourceRoleAssignmentId?: string;
  organisationId?: string;
};

const SHARE_SETTINGS_ROLES = ['NONE', 'GUEST', 'GUEST_COMMENTER'];

function deriveWorkspaceSettings(
  assignments: any[],
  roles: Role[]
): WorkspaceSettings | null {
  const noneRole = roles.find((r) => r.name === 'NONE');
  const workspaceAssignment = assignments.find(
    (a: any) => !a.public && a.organisation
  );

  if (workspaceAssignment) {
    return {
      role: workspaceAssignment.role,
      canSearch: workspaceAssignment.canSearch ?? false,
      resourceRoleAssignmentId:
        workspaceAssignment.resourceRoleAssignmentId,
      organisationId: workspaceAssignment.organisation?.organisationId
    };
  }

  return noneRole
    ? { role: noneRole, canSearch: false }
    : null;
}

export function useWorkspaceSharing(
  apiClient: any,
  videoId: string,
  organisationId: string,
  resourceType = 'Video'
) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [workspaceSettings, setWorkspaceSettings] =
    useState<WorkspaceSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!organisationId || !videoId) return;

    try {
      setLoading(true);

      const [rolesRes, assignmentsRes] = await Promise.all([
        apiClient.query({
          query: GET_RESOURCE_ROLES,
          variables: { organisationId },
          fetchPolicy: 'network-only',
          context: { module: 'users' }
        }),
        apiClient.query({
          query: GET_ROLE_ASSIGNMENTS,
          variables: { resourceId: videoId },
          fetchPolicy: 'network-only',
          context: { module: 'users' }
        })
      ]);

      const fetchedRoles: Role[] = (rolesRes.data?.getRoles || []).filter(
        (r: Role) => SHARE_SETTINGS_ROLES.includes(r.name)
      );
      const assignments = assignmentsRes.data?.getRoleAssignments || [];

      setRoles(fetchedRoles);
      setWorkspaceSettings(deriveWorkspaceSettings(assignments, fetchedRoles));
    } catch (err) {
      console.error('[useWorkspaceSharing] Failed to fetch workspace data:', err);
    } finally {
      setLoading(false);
    }
  }, [apiClient, videoId, organisationId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateWorkspaceSettings = useCallback(
    async (updatedRole: Role, canSearch: boolean) => {
      const assignmentsToUpsert = [
        {
          resourceRoleAssignmentId:
            workspaceSettings?.resourceRoleAssignmentId,
          public: false,
          canSearch,
          roleId: updatedRole.roleId,
          organisationId
        }
      ];

      const { data } = await apiClient.mutate({
        mutation: UPDATE_ROLE_ASSIGNMENTS,
        variables: {
          resourceId: videoId,
          resourceType,
          assignmentsToUpsert
        },
        context: { module: 'users' }
      });

      const updatedAssignments = data?.updateRoleAssignments || [];
      setWorkspaceSettings(deriveWorkspaceSettings(updatedAssignments, roles));

      return updatedAssignments;
    },
    [apiClient, videoId, resourceType, organisationId, workspaceSettings, roles]
  );

  return { roles, workspaceSettings, loading, updateWorkspaceSettings };
}
